import { createSlice, PayloadAction } from "@reduxjs/toolkit"

export interface FeatureToggleDefinition {
  id: string
  featureKey: string
  name: string
  description: string
  category: string
  defaultEnabled: boolean
  allowOrgOverride: boolean
  createdAt: string
  updatedAt: string
}

export interface ResolvedFeatureToggle {
  featureKey: string
  name: string
  description: string
  enabled: boolean
  source: "organization" | "global" | "default"
  allowOrgOverride: boolean
  category: string
}

export interface GlobalFeatureToggle {
  featureDefinitionId: string
  featureKey: string
  name: string
  enabled: boolean
  updatedAt: string
  updatedBy?: string
}

export interface OrganizationFeatureOverride {
  id: string
  organizationId: string
  featureDefinitionId: string
  featureKey: string
  name: string
  enabled: boolean
  createdAt: string
  updatedAt: string
  updatedBy?: string
}

interface FeatureTogglesState {
  // Feature definitions (available features)
  definitions: FeatureToggleDefinition[]
  definitionsLoading: boolean
  definitionsError: string | null

  // Resolved feature states for current organization
  resolved: ResolvedFeatureToggle[]
  resolvedLoading: boolean
  resolvedError: string | null

  // Global feature toggles (super admin only)
  global: GlobalFeatureToggle[]
  globalLoading: boolean
  globalError: string | null

  // Organization overrides (org admin only)
  overrides: OrganizationFeatureOverride[]
  overridesLoading: boolean
  overridesError: string | null

  // Last updated timestamp for cache invalidation
  lastUpdated: number | null
}

const initialState: FeatureTogglesState = {
  definitions: [],
  definitionsLoading: false,
  definitionsError: null,

  resolved: [],
  resolvedLoading: false,
  resolvedError: null,

  global: [],
  globalLoading: false,
  globalError: null,

  overrides: [],
  overridesLoading: false,
  overridesError: null,

  lastUpdated: null,
}

const featureTogglesSlice = createSlice({
  name: "featureToggles",
  initialState,
  reducers: {
    // Feature definitions actions
    setDefinitionsLoading: (state, action: PayloadAction<boolean>) => {
      state.definitionsLoading = action.payload
      if (action.payload) {
        state.definitionsError = null
      }
    },
    setDefinitions: (state, action: PayloadAction<FeatureToggleDefinition[]>) => {
      state.definitions = action.payload
      state.definitionsLoading = false
      state.definitionsError = null
      state.lastUpdated = Date.now()
    },
    setDefinitionsError: (state, action: PayloadAction<string>) => {
      state.definitionsError = action.payload
      state.definitionsLoading = false
    },

    // Resolved feature states actions
    setResolvedLoading: (state, action: PayloadAction<boolean>) => {
      state.resolvedLoading = action.payload
      if (action.payload) {
        state.resolvedError = null
      }
    },
    setResolved: (state, action: PayloadAction<ResolvedFeatureToggle[]>) => {
      state.resolved = action.payload
      state.resolvedLoading = false
      state.resolvedError = null
      state.lastUpdated = Date.now()
    },
    setResolvedError: (state, action: PayloadAction<string>) => {
      state.resolvedError = action.payload
      state.resolvedLoading = false
    },

    // Global feature toggles actions
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.globalLoading = action.payload
      if (action.payload) {
        state.globalError = null
      }
    },
    setGlobal: (state, action: PayloadAction<GlobalFeatureToggle[]>) => {
      state.global = action.payload
      state.globalLoading = false
      state.globalError = null
      state.lastUpdated = Date.now()
    },
    setGlobalError: (state, action: PayloadAction<string>) => {
      state.globalError = action.payload
      state.globalLoading = false
    },

    // Organization overrides actions
    setOverridesLoading: (state, action: PayloadAction<boolean>) => {
      state.overridesLoading = action.payload
      if (action.payload) {
        state.overridesError = null
      }
    },
    setOverrides: (state, action: PayloadAction<OrganizationFeatureOverride[]>) => {
      state.overrides = action.payload
      state.overridesLoading = false
      state.overridesError = null
      state.lastUpdated = Date.now()
    },
    setOverridesError: (state, action: PayloadAction<string>) => {
      state.overridesError = action.payload
      state.overridesLoading = false
    },

    // Update individual feature states
    updateResolvedFeature: (state, action: PayloadAction<{ featureKey: string; enabled: boolean; source: ResolvedFeatureToggle["source"] }>) => {
      const { featureKey, enabled, source } = action.payload
      const featureIndex = state.resolved.findIndex(f => f.featureKey === featureKey)
      if (featureIndex !== -1) {
        state.resolved[featureIndex].enabled = enabled
        state.resolved[featureIndex].source = source
        state.lastUpdated = Date.now()
      }
    },

    updateGlobalFeature: (state, action: PayloadAction<{ featureKey: string; enabled: boolean }>) => {
      const { featureKey, enabled } = action.payload
      const featureIndex = state.global.findIndex(f => f.featureKey === featureKey)
      if (featureIndex !== -1) {
        state.global[featureIndex].enabled = enabled
        state.global[featureIndex].updatedAt = new Date().toISOString()
        state.lastUpdated = Date.now()
      }
    },

    addOrganizationOverride: (state, action: PayloadAction<OrganizationFeatureOverride>) => {
      const existingIndex = state.overrides.findIndex(
        o => o.featureKey === action.payload.featureKey && o.organizationId === action.payload.organizationId
      )
      if (existingIndex !== -1) {
        state.overrides[existingIndex] = action.payload
      } else {
        state.overrides.push(action.payload)
      }
      state.lastUpdated = Date.now()
    },

    removeOrganizationOverride: (state, action: PayloadAction<{ featureKey: string; organizationId: string }>) => {
      const { featureKey, organizationId } = action.payload
      state.overrides = state.overrides.filter(
        o => !(o.featureKey === featureKey && o.organizationId === organizationId)
      )
      state.lastUpdated = Date.now()
    },

    // Clear all data (on logout/organization change)
    clearFeatureToggles: (state) => {
      return initialState
    },

    // Clear organization-specific data (on organization change)
    clearOrganizationData: (state) => {
      state.resolved = []
      state.resolvedLoading = false
      state.resolvedError = null
      state.overrides = []
      state.overridesLoading = false
      state.overridesError = null
      state.lastUpdated = Date.now()
    },
  },
})

export const {
  setDefinitionsLoading,
  setDefinitions,
  setDefinitionsError,
  setResolvedLoading,
  setResolved,
  setResolvedError,
  setGlobalLoading,
  setGlobal,
  setGlobalError,
  setOverridesLoading,
  setOverrides,
  setOverridesError,
  updateResolvedFeature,
  updateGlobalFeature,
  addOrganizationOverride,
  removeOrganizationOverride,
  clearFeatureToggles,
  clearOrganizationData,
} = featureTogglesSlice.actions

export default featureTogglesSlice.reducer

// Selectors
export const selectFeatureToggles = (state: { featureToggles: FeatureTogglesState }) => state.featureToggles

export const selectFeatureToggleDefinitions = (state: { featureToggles: FeatureTogglesState }) => 
  state.featureToggles.definitions

export const selectResolvedFeatureToggles = (state: { featureToggles: FeatureTogglesState }) => 
  state.featureToggles.resolved

export const selectGlobalFeatureToggles = (state: { featureToggles: FeatureTogglesState }) => 
  state.featureToggles.global

export const selectOrganizationFeatureOverrides = (state: { featureToggles: FeatureTogglesState }) => 
  state.featureToggles.overrides

// Helper selector to check if a feature is enabled
export const selectIsFeatureEnabled = (featureKey: string) => (state: { featureToggles: FeatureTogglesState }) => {
  const feature = state.featureToggles.resolved.find(f => f.featureKey === featureKey)
  return feature?.enabled ?? false
}

// Helper selector to get feature state with metadata
export const selectFeatureState = (featureKey: string) => (state: { featureToggles: FeatureTogglesState }) => {
  return state.featureToggles.resolved.find(f => f.featureKey === featureKey)
}

// Loading state selectors
export const selectFeatureTogglesLoading = (state: { featureToggles: FeatureTogglesState }) => 
  state.featureToggles.resolvedLoading || state.featureToggles.definitionsLoading

export const selectFeatureTogglesError = (state: { featureToggles: FeatureTogglesState }) => 
  state.featureToggles.resolvedError || state.featureToggles.definitionsError
